// src/appbuilder/CloneDigitalTwinPanel.js
//
// "Clone Your Digital Twin" —— 触发于右下角 Lex AI 浮动 pill 进入 twin 模式时。
// 视觉：与 LexAIPanel.css 同款 Vision Pro 液态玻璃语言（独立 cdtp- 前缀，
//        逻辑完全解耦）。
//
// 6 个 phase：
//   - scanning   登录用户首次打开，跑 collectionGroup("listings")
//   - found      扫到 N≥1 个 digital twin，列表展示，可一键 Use this
//   - empty      登录但 N=0，温暖空状态 + Create my profile
//   - signedOut  未登录，Sign in 主 CTA + sign-up 次 CTA
//   - error      扫描失败，Retry + Create 兜底
//
// 隐私：authorUid==viewerUid 严格过滤，无任何 % / 计数 / pool 暴露；
//        所有发布动作走 ListingPage，本面板不写任何东西。

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { getFirestore } from "firebase/firestore";
import "./CloneDigitalTwinPanel.css";
import { scanMyDigitalTwins } from "./digital-twin-helpers";

const SWIPE_DISMISS_THRESHOLD = 80; // 下滑 80px 关闭

/* ─── 主组件 ─────────────────────────────────────────────────────── */

/**
 * @param {Object} props
 * @param {string} props.targetAppId        - 当前 app（结果中会跳过它）
 * @param {string} props.viewerUid          - 登录用户 uid（空 → signedOut 状态）
 * @param {Function} props.onClose          - 关闭面板
 * @param {Function} [props.onPickTwin]     - 选 twin 的自定义 callback (twin) => Promise|void
 *                                            不传 → 默认跳到 listing 页（适合右下角 pill 入口）
 *                                            传 → 调用方接管，做 inline clone（适合 Inbox / Profile icon）
 * @param {string} [props.primaryActionLabel] - 卡片右侧主按钮文字，默认 "Use this →"
 * @param {number} [props.inlineProgress]   - 0..100，> 0 时面板底部出现蓝色进度条
 * @param {boolean} [props.busy]            - 操作中（disable close / 卡片）
 * @param {Function} [props.onStartFresh]   - 自定义"start fresh"行为（不传走默认跳 listing 页）
 */
export default function CloneDigitalTwinPanel({
  targetAppId,
  viewerUid,
  onClose,
  onPickTwin,
  primaryActionLabel,
  inlineProgress,
  busy = false,
  onStartFresh,
}) {
  const navigate = useNavigate();
  const isLoggedIn = !!viewerUid;

  // phase: scanning | found | empty | signedOut | error
  const [phase, setPhase] = useState(isLoggedIn ? "scanning" : "signedOut");
  const [twins, setTwins] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  // 拖拽下滑关闭
  const panelRef = useRef(null);
  const dragStateRef = useRef(null);

  // Body scroll lock —— 面板打开时锁住背景滚动
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // 扫描（仅登录态）
  const scanKey = useMemo(
    () => `${viewerUid || ""}::${targetAppId || ""}`,
    [viewerUid, targetAppId]
  );
  useEffect(() => {
    let cancelled = false;
    if (!viewerUid) {
      setPhase("signedOut");
      return undefined;
    }
    setPhase("scanning");
    setErrorMsg("");
    (async () => {
      try {
        const db = getFirestore();
        const sorted = await scanMyDigitalTwins({
          db,
          viewerUid,
          currentAppId: targetAppId,
        });
        if (cancelled) return;
        setTwins(sorted);
        setPhase(sorted.length ? "found" : "empty");
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn("[CloneDigitalTwinPanel] scan failed:", err);
        setErrorMsg(String(err?.message || err || "Scan failed"));
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scanKey, viewerUid, targetAppId]);

  // 跳转 helpers
  const handleSignInThen = (afterPath) => {
    const back = afterPath || `/apps/${targetAppId || ""}`;
    navigate(`/login?next=${encodeURIComponent(back)}`);
    onClose?.();
  };

  const handleUseTwin = async (twin) => {
    if (!isLoggedIn) {
      handleSignInThen(`/apps/${targetAppId}/listing/dating`);
      return;
    }
    // ✨ 调用方接管 → inline clone 模式（Inbox / Profile icon 入口走这里）
    if (typeof onPickTwin === "function") {
      try {
        await onPickTwin(twin);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[CloneDigitalTwinPanel] onPickTwin failed:", err);
      }
      return;
    }
    // 默认行为 → 跳到 listing 页让用户审核 + 主动 publish（右下角 pill 入口）
    if (!targetAppId) return;
    navigate(`/apps/${targetAppId}/listing/dating`, {
      state: {
        prefilledFrom: {
          ownerUid: twin.ownerUid,
          appId: twin.appId,
          listingId: twin.listingId,
        },
      },
    });
    onClose?.();
  };

  const handleStartFresh = () => {
    if (!isLoggedIn) {
      handleSignInThen(`/apps/${targetAppId}/listing/dating`);
      return;
    }
    if (typeof onStartFresh === "function") {
      onStartFresh();
      return;
    }
    if (!targetAppId) return;
    navigate(`/apps/${targetAppId}/listing/dating`);
    onClose?.();
  };

  const handleRetry = () => {
    if (!viewerUid) return;
    // 通过更新 scanKey 触发重扫
    setPhase("scanning");
    setErrorMsg("");
    // 强制 useEffect 重跑：用一个 nonce trick
    setTwins((t) => [...t]);
  };

  // ─── 拖拽下滑关闭（移动端） ─────────────────
  const onPointerDown = (e) => {
    // 只对 handle 区域响应
    if (!e.currentTarget?.classList?.contains?.("cdtp-handle")) return;
    dragStateRef.current = {
      startY: e.clientY,
      currentY: e.clientY,
      pointerId: e.pointerId,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}
  };
  const onPointerMove = (e) => {
    const s = dragStateRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    const dy = Math.max(0, e.clientY - s.startY);
    s.currentY = e.clientY;
    if (panelRef.current) {
      panelRef.current.style.transform = `translateY(${dy}px)`;
      panelRef.current.style.transition = "none";
    }
  };
  const onPointerUp = (e) => {
    const s = dragStateRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    const dy = Math.max(0, s.currentY - s.startY);
    dragStateRef.current = null;
    if (panelRef.current) {
      panelRef.current.style.transition = "transform .22s ease";
      panelRef.current.style.transform = "translateY(0)";
    }
    if (dy > SWIPE_DISMISS_THRESHOLD) {
      onClose?.();
    }
  };

  // ESC 关闭
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // ─── 渲染 ──────────────────────────────────────
  const modal = (
    <div
      className="cdtp-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Clone your digital twin"
    >
      <button
        type="button"
        className="cdtp-scrim"
        aria-label="Close"
        onClick={busy ? undefined : onClose}
        disabled={busy}
      />

      <div className="cdtp-panel" ref={panelRef}>
        {/* Drag-handle（移动端，桌面通过 media query 隐藏） */}
        <div
          className="cdtp-handle"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span className="cdtp-handle__bar" aria-hidden />
        </div>

        {/* 顶部条 */}
        <div className="cdtp-topbar">
          <div className="cdtp-title">
            <strong>Clone Your Digital Twin</strong>
          </div>
          <button
            type="button"
            className="cdtp-close"
            aria-label="Close"
            onClick={onClose}
            disabled={busy}
          >
            <span aria-hidden>×</span>
          </button>
        </div>

        {/* Inline progress bar —— 由调用方通过 inlineProgress (0..100) 驱动 */}
        {Number.isFinite(+inlineProgress) && +inlineProgress > 0 ? (
          <div className="cdtp-progress" aria-hidden="true">
            <div
              className="cdtp-progress__fill"
              style={{ width: `${Math.min(100, Math.max(0, +inlineProgress))}%` }}
            />
          </div>
        ) : null}

        {/* 主体 */}
        <div className="cdtp-body">
          {phase === "scanning" && (
            <div className="cdtp-scan">
              <div className="cdtp-scan__cards" aria-hidden>
                <div className="cdtp-scan__card" />
                <div className="cdtp-scan__card" />
                <div className="cdtp-scan__card" />
              </div>
              <div className="cdtp-scan__text">
                Looking across your other apps…
              </div>
            </div>
          )}

          {phase === "found" && (
            <>
              <div className="cdtp-list" role="list">
                {twins.map((t) => (
                  <div
                    className={"cdtp-card" + (busy ? " cdtp-card--busy" : "")}
                    role="listitem"
                    key={t.path}
                    onClick={() => { if (!busy) handleUseTwin(t); }}
                  >
                    <div className="cdtp-card__avatar">
                      {t.avatar ? (
                        <img
                          src={t.avatar}
                          alt=""
                          draggable={false}
                          onDragStart={(e) => e.preventDefault()}
                        />
                      ) : (
                        <span className="cdtp-card__avatar-fallback">
                          {(t.name?.[0] || "U").toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="cdtp-card__meta">
                      <div className="cdtp-card__name">{t.name}</div>
                      <div className="cdtp-card__sub">
                        <span className="cdtp-card__app">{t.appLabel}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="cdtp-card__use"
                      disabled={busy}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (busy) return;
                        handleUseTwin(t);
                      }}
                    >
                      {primaryActionLabel || (
                        <>Use this <span aria-hidden>→</span></>
                      )}
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="cdtp-fresh"
                onClick={handleStartFresh}
              >
                Or start fresh →
              </button>
            </>
          )}

          {phase === "empty" && (
            <div className="cdtp-empty">
              <div className="cdtp-empty__art" aria-hidden>
                <span className="cdtp-empty__orb" />
              </div>
              <div className="cdtp-empty__title">You’re new to Lex 👋</div>
              <div className="cdtp-empty__msg">
                No digital twins yet — let’s spin up your first profile here.
              </div>
              <button
                type="button"
                className="cdtp-cta"
                onClick={handleStartFresh}
              >
                Create my profile
              </button>
            </div>
          )}

          {phase === "signedOut" && (
            <div className="cdtp-empty">
              <div className="cdtp-empty__art" aria-hidden>
                <span className="cdtp-empty__orb" />
              </div>
              <div className="cdtp-empty__title">Sign in to clone your twin</div>
              <button
                type="button"
                className="cdtp-cta"
                onClick={() => handleSignInThen(`/apps/${targetAppId || ""}`)}
              >
                Sign in
              </button>
              <button
                type="button"
                className="cdtp-fresh"
                onClick={handleStartFresh}
              >
                Or sign up & create my profile →
              </button>
            </div>
          )}

          {phase === "error" && (
            <div className="cdtp-empty">
              <div className="cdtp-empty__art" aria-hidden>
                <span className="cdtp-empty__orb" />
              </div>
              <div className="cdtp-empty__title">Hmm, scan didn’t finish</div>
              <div className="cdtp-empty__msg">{errorMsg}</div>
              <button
                type="button"
                className="cdtp-cta"
                onClick={handleRetry}
              >
                Try again
              </button>
              <button
                type="button"
                className="cdtp-fresh"
                onClick={handleStartFresh}
              >
                Or create my profile →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
